// client/src/hooks/support/useAssigneeRanking.ts
import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { isSoftDeleted } from '../../lib/appsheet/softDelete';
import { useOrganization } from '../../contexts/OrganizationContext';

/**
 * Context determines which collections to count for ranking.
 * - 'support': open call logs + cases (default)
 * - 'leads': open leads
 * - 'service_tickets': open service tickets
 * - 'cases': open cases only
 * - 'call_logs': open call logs only
 */
export type RankingContext = 'support' | 'leads' | 'service_tickets' | 'cases' | 'call_logs';

/**
 * Fetches assignment frequency counts for active users in a given context.
 * Returns a Map<userId, count> for sorting assignee dropdowns.
 * Cached for 10 minutes.
 */
export function useAssigneeRanking(context: RankingContext = 'support') {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;

  return useQuery({
    queryKey: ['assigneeRanking', orgId, context],
    queryFn: async (): Promise<Map<string, number>> => {
      if (!orgId) return new Map();

      const counts = new Map<string, number>();

      const queries = getQueriesForContext(orgId, context);
      const snapshots = await Promise.all(queries.map(q => getDocs(q.query)));

      snapshots.forEach((snap, i) => {
        const field = queries[i].assigneeField;
        for (const doc of snap.docs) {
          // Soft-deleted AppSheet rows must not count toward assignment load
          // (no-op for native collections, which never carry _deleted).
          if (isSoftDeleted(doc.data() as Record<string, unknown>)) continue;
          const uid = (doc.data() as Record<string, unknown>)[field] as string | undefined;
          if (uid) counts.set(uid, (counts.get(uid) ?? 0) + 1);
        }
      });

      return counts;
    },
    enabled: !!orgId,
    staleTime: 10 * 60 * 1000,
  });
}

interface RankingQuery {
  query: ReturnType<typeof query>;
  assigneeField: string;
}

function getQueriesForContext(orgId: string, context: RankingContext): RankingQuery[] {
  const openCallLogs = {
    query: query(
      collection(db, COLLECTIONS.CALL_LOGS),
      where('organizationId', '==', orgId),
      where('status', 'in', ['1 New Inquiry', '2 Open - Need to Call Back', '2 Open - Contacted Waiting on Us']),
    ),
    assigneeField: 'assignedToUserId',
  };

  const openCases = {
    query: query(
      collection(db, COLLECTIONS.SUPPORT_CASES),
      where('organizationId', '==', orgId),
      where('status', 'in', ['new', 'open', 'pending', 'on_hold']),
    ),
    assigneeField: 'assigneeId',
  };

  const openLeads = {
    query: query(
      collection(db, COLLECTIONS.LEADS),
      where('organizationId', '==', orgId),
      where('status', 'in', ['new', 'contacted', 'qualified', 'proposal']),
    ),
    assigneeField: 'ownerId',
  };

  const openTickets = {
    query: query(
      collection(db, COLLECTIONS.APPSHEET_SERVICE_LOGS),
      where('Status', 'in', [
        '1 Scheduled',
        '2 In Progress',
        '3 Needs Parts',
        '4 Waiting on Customer',
      ]),
    ),
    assigneeField: 'Assigned To',
  };

  switch (context) {
    case 'support': return [openCallLogs, openCases];
    case 'call_logs': return [openCallLogs];
    case 'cases': return [openCases];
    case 'leads': return [openLeads];
    case 'service_tickets': return [openTickets];
    default: return [openCallLogs, openCases];
  }
}

/**
 * Sort members by assignment frequency (most assigned first),
 * with unranked members at the end sorted alphabetically.
 */
export function sortMembersByAssignmentFrequency<T extends { userId: string; displayName: string }>(
  members: T[],
  ranking: Map<string, number> | undefined
): T[] {
  if (!ranking || ranking.size === 0) return members;

  return [...members].sort((a, b) => {
    const aCount = ranking.get(a.userId) ?? 0;
    const bCount = ranking.get(b.userId) ?? 0;

    if (aCount > 0 && bCount > 0) return bCount - aCount;
    if (aCount > 0) return -1;
    if (bCount > 0) return 1;
    return a.displayName.localeCompare(b.displayName);
  });
}
